import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { CreditCard, ExternalLink, QrCode, Radio, BarChart3, User, X } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserDigitalCards() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assigningProfile, setAssigningProfile] = useState({});
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCards();
    fetchProfiles();
  }, [user, navigate]);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/digital-profiles`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setProfiles(response.data.profiles || []);
      }
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
    }
  };

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Filter for digital cards only (those with type: 'digital-card')
        const allCards = response.data.cards || [];
        const digitalCards = allCards.filter(card => card.order_data?.type === 'digital-card');
        
        // Fetch profile username for each card
        const cardsWithProfile = await Promise.all(
          digitalCards.map(async (card) => {
            try {
              const profileResponse = await apiRequest(`${BACKEND_URL}/api/user/digital-card-profile/${card.transaction_id}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });
              
              if (profileResponse.ok && profileResponse.data) {
                return {
                  ...card,
                  profile_username: profileResponse.data.username || null
                };
              }
            } catch (err) {
              console.error('Failed to fetch profile for card:', card.transaction_id);
            }
            
            // Check if profile is assigned via dynamic link
            if (card.dynamic_link_details?.associated_profile_username) {
              // Find the profile ID from username
              const associatedProfile = profiles.find(p => p.username === card.dynamic_link_details.associated_profile_username);
              return {
                ...card,
                assigned_profile: associatedProfile,
                profile_username: card.dynamic_link_details.associated_profile_username
              };
            }
            
            return card;
          })
        );
        
        setCards(cardsWithProfile);
      } else {
        setError('Failed to fetch cards');
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleProfileAssignment = async (cardId, profileId) => {
    if (!profileId) return;
    
    setAssigningProfile({ ...assigningProfile, [cardId]: true });
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/cards/${cardId}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profile_id: profileId }),
      });

      if (response.ok) {
        // Update local state with the assigned profile
        const selectedProfile = profiles.find(p => p.id === profileId);
        if (selectedProfile) {
          setCards(cards.map(card => 
            card.transaction_id === cardId 
              ? { ...card, assigned_profile: selectedProfile, profile_username: selectedProfile.username }
              : card
          ));
        }
        setSuccess('Profile assigned successfully!');
        setTimeout(() => setSuccess(''), 3000);
        
        // Close modal and refresh
        setShowProfileModal(false);
        setSelectedCard(null);
        setSelectedProfileId('');
        fetchCards();
      } else {
        throw new Error(response.data?.detail || 'Failed to assign profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to assign profile');
      setTimeout(() => setError(''), 3000);
    } finally {
      setAssigningProfile({ ...assigningProfile, [cardId]: false });
    }
  };

  const openProfileModal = (card) => {
    setSelectedCard(card);
    // Set currently assigned profile if any
    if (card.assigned_profile?.id) {
      setSelectedProfileId(card.assigned_profile.id);
    } else if (card.dynamic_link_details?.associated_profile_username) {
      const assignedProfile = profiles.find(p => p.username === card.dynamic_link_details.associated_profile_username);
      if (assignedProfile) {
        setSelectedProfileId(assignedProfile.id);
      }
    }
    setShowProfileModal(true);
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedCard(null);
    setSelectedProfileId('');
  };

  const handleModalAssign = () => {
    if (selectedCard && selectedProfileId) {
      handleProfileAssignment(selectedCard.transaction_id, selectedProfileId);
    }
  };

  const getAssignedProfile = (card) => {
    // Check if profile is assigned via the card's assigned_profile
    if (card.assigned_profile) {
      return card.assigned_profile;
    }
    
    // Check if profile is assigned via dynamic link
    if (card.dynamic_link_details?.associated_profile_username) {
      return profiles.find(p => p.username === card.dynamic_link_details.associated_profile_username);
    }
    
    return null;
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading cards...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-navy">My Digital Cards</h1>
          <p className="text-gray-600 mt-2">View and manage your delivered digital cards</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {cards.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div key={card.transaction_id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                {/* Card Header */}
                <div className="bg-gradient-to-br from-navy to-purple-600 p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">
                        {card.order_data?.cardName || card.order_data?.displayName || 'Digital Card'}
                      </h3>
                      <p className="text-sm opacity-90 mt-1">
                        {card.order_data?.displayName && card.order_data?.cardName ? card.order_data.displayName : (card.order_data?.cardType || 'Digital Card')}
                      </p>
                    </div>
                    <CreditCard className="w-8 h-8 opacity-80" />
                  </div>
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <span className="px-2 py-1 bg-white/20 rounded">
                      {card.order_data?.selectedMaterial?.name || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-mono font-semibold">#{card.transaction_id?.slice(0, 10)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivered:</span>
                      <span className="font-semibold text-green-600">
                        {new Date(card.updated_at || card.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Card Tracking Stats */}
                  {card.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4 space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Card Statistics</p>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="flex items-center gap-1 p-2 bg-green-50 rounded">
                          <QrCode className="w-3 h-3 text-green-600" />
                          <span className="text-green-700">QR: {card.dynamic_link_details.qr_scans || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 p-2 bg-purple-50 rounded">
                          <Radio className="w-3 h-3 text-purple-600" />
                          <span className="text-purple-700">NFC: {card.dynamic_link_details.nfc_taps || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 p-2 bg-blue-50 rounded">
                          <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <span className="text-blue-700">Direct: {card.dynamic_link_details.direct_opens || 0}</span>
                        </div>
                      </div>

                      {/* Profile URL - Show if profile is assigned */}
                      {(() => {
                        const assignedProfile = getAssignedProfile(card);
                        const profileUsername = assignedProfile?.username || card.profile_username;
                        
                        return profileUsername ? (
                          <div className="text-xs text-gray-600">
                            <p className="mb-1 font-semibold text-navy">Your Profile Link:</p>
                            <a
                              href={`/profile/${profileUsername}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline truncate block break-all"
                            >
                              {`${window.location.origin}/profile/${profileUsername}`}
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/profile/${profileUsername}`);
                                alert('Profile link copied to clipboard!');
                              }}
                              className="mt-2 text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded hover:bg-blue-100"
                            >
                              Copy Link
                            </button>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {!card.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-gray-500 text-center">Card will be activated once delivered</p>
                    </div>
                  )}
                  
                  {/* Assigned Profile Display & Actions */}
                  <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
                    {/* Show Assigned Profile */}
                    {(() => {
                      const assignedProfile = getAssignedProfile(card);
                      return assignedProfile ? (
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <div>
                                <p className="text-xs font-semibold text-blue-900">Assigned Profile</p>
                                <p className="text-sm font-bold text-blue-700">{assignedProfile.profile_name}</p>
                                <p className="text-xs text-blue-600">@{assignedProfile.username}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => openProfileModal(card)}
                              className="text-xs bg-white text-blue-600 px-3 py-1 rounded border border-blue-300 hover:bg-blue-50"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => openProfileModal(card)}
                          className="w-full bg-navy text-white py-3 rounded-lg hover:bg-navy/90 transition-colors font-semibold flex items-center justify-center gap-2"
                        >
                          <User className="w-5 h-5" />
                          Assign Profile
                        </button>
                      );
                    })()}

                    {/* View Analytics Button */}
                    <button
                      onClick={() => navigate(`/user/digital-cards/${card.transaction_id}/analytics`)}
                      className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <BarChart3 className="w-5 h-5" />
                      View Analytics
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Cards Yet</h3>
            <p className="text-gray-600 mb-6">
              Your delivered cards will appear here. Start by placing an order!
            </p>
            <button
              onClick={() => navigate('/review-cards')}
              className="px-6 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors"
            >
              Browse Cards
            </button>
          </div>
        )}

        {/* Profile Assignment Modal */}
        {showProfileModal && selectedCard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-navy">Assign Profile</h3>
                <button
                  onClick={closeProfileModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Card Info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600">Card:</p>
                <p className="font-semibold text-navy">{selectedCard.order_data?.displayName || 'Business Card'}</p>
                <p className="text-xs text-gray-500 mt-1">Order #{selectedCard.transaction_id?.slice(0, 10)}</p>
              </div>

              {/* Profile Selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-navy mb-2">
                  Select Digital Profile
                </label>
                <select
                  value={selectedProfileId}
                  onChange={(e) => setSelectedProfileId(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                >
                  <option value="">-- Choose a profile --</option>
                  {profiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.profile_name} (@{profile.username})
                    </option>
                  ))}
                </select>
                
                {profiles.length === 0 && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      No profiles available. 
                      <button 
                        onClick={() => {
                          closeProfileModal();
                          navigate('/user/digital-profiles/create');
                        }} 
                        className="text-blue-600 hover:underline ml-1 font-semibold"
                      >
                        Create one now
                      </button>
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3">
                <button
                  onClick={closeProfileModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleModalAssign}
                  disabled={!selectedProfileId || assigningProfile[selectedCard.transaction_id]}
                  className="flex-1 px-4 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {assigningProfile[selectedCard.transaction_id] ? 'Assigning...' : 'Assign Profile'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
